import React, { JSXElementConstructor, memo, useMemo } from 'react';
import styled from 'styled-components';
import {
    Box,
    Hidden,
    HiddenProps,
    Pagination,
    Typography,
} from '@mui/material';
import DataTableErrorState from './data-table-error-state';
import {
    DataGrid,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridCallbackDetails,
    GridColDef,
    GridRowParams,
    MuiEvent,
} from '@mui/x-data-grid';

const EMPTY_OVERLAY_HEIGHT = 140;
const EMPTY_GRID_HEIGHT = 190;

const StyledGridOverlay = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    paddingBlock: '12px',
    boxSizing: 'border-box',
}));

type MuiDataTableProps = {
    rows: any[];
    columns: GridColDef[];
    isLoading?: boolean;
    isSuccess?: boolean;
    isError?: boolean;
    error?: unknown;
    onRefetch?: () => void;
    noRowsMessage?: string;
    paginateInfo?: any;
    setPage?: any;
    onClickFunction?: (
        params: GridRowParams<any>,
        event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
        details: GridCallbackDetails<any>,
    ) => void;
    hideHeader?: JSXElementConstructor<HiddenProps>;
    checkBox?: {
        selectedItems: any[];
        onCheckBox: any;
    };
    headerStyle?: 'light' | 'dark';
};

const MuiDataTable = memo(
    ({
        rows,
        columns,
        isLoading,
        isSuccess,
        isError,
        error,
        onRefetch,
        paginateInfo,
        setPage,
        noRowsMessage,
        checkBox,
        headerStyle,
        onClickFunction,
        hideHeader,
    }: MuiDataTableProps) => {
        const colsWithDash = useMemo<GridColDef[]>(() => {
            const defaultFlex = 1 / columns.length;

            // const withDash =
            //     (userRend?: (p: GridRenderCellParams) => React.ReactNode) =>
            //     (p: GridRenderCellParams) => {
            //         const out = userRend?.(p) ?? p.value;
            //         return (out ?? '') === '' ? '–' : out;
            //     };

            return columns.map((col) => ({
                ...col,
                flex: col.flex ?? (col.width ? undefined : defaultFlex),
                minWidth: col.minWidth ?? 80,
                sortable: col.sortable ?? false,
                headerClassName: 'MUIheaderClassName',
                renderCell: col.renderCell,
            }));
        }, [columns]);

        const selectableColumns = useMemo<GridColDef[]>(
            () => [
                ...colsWithDash,
                {
                    ...GRID_CHECKBOX_SELECTION_COL_DEF,
                    width: 100,
                },
            ],
            [colsWithDash],
        );

        const isNoRowsState =
            (rows?.length ?? 0) === 0 && !isLoading && !!isSuccess;

        const CustomFooter = () =>
            paginateInfo?.total_pages > 1 ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'border.main',
                    }}>
                    {setPage && (
                        <Pagination
                            size="small"
                            siblingCount={0}
                            count={paginateInfo!.total_pages}
                            page={paginateInfo!.current_page}
                            onChange={(_, v) => setPage(v)}
                        />
                    )}
                </Box>
            ) : null;

        const CustomNoRowsOverlay = () => (
            <StyledGridOverlay>
                <Box
                    component="img"
                    src="/images/no-exist.png"
                    alt=""
                    sx={(theme) => ({
                        width: 82,
                        height: 82,
                        objectFit: 'contain',
                        mb: 1.5,
                        opacity: 0.92,
                        filter:
                            theme.palette.mode === 'light'
                                ? 'brightness(0.4)'
                                : 'none',
                    })}
                />
                <Typography
                    variant="body2"
                    sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    {noRowsMessage ?? 'هیچ داده‌ای جهت نمایش وجود ندارد.'}
                </Typography>
            </StyledGridOverlay>
        );


        const hasError = Boolean(isError) || (!isLoading && isSuccess === false);

        if (hasError) {
            return <DataTableErrorState error={error} onRefetch={onRefetch} />;
        }

        return (
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                }}>
                <DataGrid
                    autoHeight={!isNoRowsState}
                    rowHeight={54}
                    columnHeaderHeight={48}
                    rows={rows ?? []}
                    columns={checkBox ? selectableColumns : colsWithDash}
                    loading={!!isLoading}
                    disableColumnMenu
                    checkboxSelection={!!checkBox}
                    rowSelectionModel={checkBox?.selectedItems ?? undefined}
                    onRowSelectionModelChange={(newSelection) => {
                        !!checkBox && checkBox?.onCheckBox(newSelection);
                    }}
                    onRowClick={(params, event, details) => {
                        onClickFunction &&
                            onClickFunction(params, event, details);
                    }}
                    slots={{
                        footer: CustomFooter,
                        noRowsOverlay: CustomNoRowsOverlay,
                        columnHeaders: hideHeader && Hidden,
                    }}
                    sx={{
                        width: '100%',
                        '& .ltr-cell': {
                            direction: 'ltr',
                            textAlign: 'start',
                            fontVariantNumeric: 'tabular-nums',
                        },
                        '& .amount-cell': {
                            fontWeight: 650,
                            fontVariantNumeric: 'tabular-nums',
                        },
                        '& .MuiDataGrid-columnSeparator': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-cellContent:empty::before': {
                            content: '"–"',
                            color: 'text.disabled',
                        },
                        ...(isNoRowsState
                            ? {
                                  height: `${EMPTY_GRID_HEIGHT}px`,
                                  minHeight: `${EMPTY_GRID_HEIGHT}px`,
                                  maxHeight: `${EMPTY_GRID_HEIGHT}px`,
                                  '--DataGrid-overlayHeight': `${EMPTY_OVERLAY_HEIGHT}px`,
                                  '& .MuiDataGrid-main': {
                                      minHeight: 0,
                                  },
                                  '& .MuiDataGrid-virtualScroller': {
                                      overflow: 'hidden !important',
                                  },
                                  '& .MuiDataGrid-virtualScrollerContent': {
                                      height: `${EMPTY_OVERLAY_HEIGHT}px !important`,
                                      minHeight: `${EMPTY_OVERLAY_HEIGHT}px !important`,
                                  },
                                  '& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner': {
                                      height: `${EMPTY_OVERLAY_HEIGHT}px !important`,
                                      minHeight: `${EMPTY_OVERLAY_HEIGHT}px !important`,
                                      maxHeight: `${EMPTY_OVERLAY_HEIGHT}px !important`,
                                  },
                              }
                            : {}),

                        '&': {
                            border: '1px solid',
                            borderColor: 'border.light',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            backgroundColor: 'background.paper',
                            boxShadow: (theme) =>
                                theme.palette.mode === 'light'
                                    ? '0 10px 30px rgba(23, 32, 51, 0.04)'
                                    : 'none',
                        },
                        '& .MuiDataGrid-row': {
                            transition: 'background-color 140ms ease',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                            '&:last-child': {
                                '& .MuiDataGrid-cell': {
                                    borderBottom: 'none',
                                },
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            borderColor: 'border.light',
                            outline: 'none !important',
                            paddingInline: { xs: '12px', md: '16px' },
                            '&:first-of-type': {
                                paddingInlineStart: '20px',
                            },
                            '&:last-of-type': {
                                paddingInlineEnd: '20px',
                            },
                            cursor: !onClickFunction ? 'default' : 'pointer',
                        },
                        '& .no-header': {
                            display: 'none',
                            border: 'none',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            outline: 'none !important',
                            backgroundColor:
                                headerStyle === 'dark'
                                    ? 'background.light'
                                    : 'background.extraLight',
                            borderRadius: '0px !important',
                            borderBottom: '1px solid',
                            borderColor: 'border.main',
                            paddingInline: { xs: '12px', md: '16px' },
                            '&:first-of-type': {
                                paddingInlineStart: '20px',
                            },
                            '&:last-of-type': {
                                paddingInlineEnd: '20px',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                color: 'text.secondary',
                                fontWeight: 650,
                            },
                        },
                        a: {
                            color: 'primary.main',
                        },
                        color: 'text.primary',
                    }}
                />
            </Box>
        );
    },
);

export default MuiDataTable;
